import { useState, useRef, useCallback, useEffect } from 'react';

const ICE_SERVERS = [
	{ urls: 'stun:stun.l.google.com:19302' },
	{ urls: 'stun:stun1.l.google.com:19302' },
];

// Mesh WebRTC manager: one RTCPeerConnection per remote participant
export function useWebRTC({ localStreamRef, onRemoteStream, onParticipantLeft, socket, roomId } = {}) {
	const peerConnections = useRef({}); // userId -> RTCPeerConnection
	const [remoteStreams, setRemoteStreams] = useState({}); // userId -> MediaStream

	const createPeerConnection = useCallback((targetUserId) => {
		if (peerConnections.current[targetUserId]) {
			return peerConnections.current[targetUserId];
		}

		const pc = new RTCPeerConnection({ iceServers: ICE_SERVERS });

		// Add local tracks
		const stream = localStreamRef?.current;
		if (stream) {
			stream.getTracks().forEach((track) => pc.addTrack(track, stream));
		}

		// Send ICE candidates via socket
		pc.onicecandidate = ({ candidate }) => {
			if (!candidate || !socket) return;
			socket.emit('call:ice-candidate', { to: targetUserId, candidate, roomId });
		};

		// Receive remote stream
		pc.ontrack = ({ streams }) => {
			const remoteStream = streams[0];
			if (!remoteStream) return;
			setRemoteStreams((prev) => ({ ...prev, [targetUserId]: remoteStream }));
			if (typeof onRemoteStream === 'function') onRemoteStream(targetUserId, remoteStream);
		};

		pc.onconnectionstatechange = () => {
			if (['disconnected', 'failed', 'closed'].includes(pc.connectionState)) {
				closePeerConnection(targetUserId);
			}
		};

		peerConnections.current[targetUserId] = pc;
		return pc;
	}, [localStreamRef, roomId, socket, onRemoteStream]);

	const createOffer = useCallback(async (targetUserId) => {
		if (!socket) return;
		const pc = createPeerConnection(targetUserId);
		try {
			const offer = await pc.createOffer();
			await pc.setLocalDescription(offer);
			socket.emit('call:offer', { to: targetUserId, offer, roomId });
		} catch (err) {
			console.error('useWebRTC createOffer error:', err);
		}
	}, [createPeerConnection, roomId, socket]);

	const handleOffer = useCallback(async (fromUserId, offer) => {
		if (!socket) return;
		const pc = createPeerConnection(fromUserId);
		try {
			await pc.setRemoteDescription(new RTCSessionDescription(offer));
			const answer = await pc.createAnswer();
			await pc.setLocalDescription(answer);
			socket.emit('call:answer', { to: fromUserId, answer, roomId });
		} catch (err) {
			console.error('useWebRTC handleOffer error:', err);
		}
	}, [createPeerConnection, roomId, socket]);

	const handleAnswer = useCallback(async (fromUserId, answer) => {
		const pc = peerConnections.current[fromUserId];
		if (!pc) return;
		try {
			await pc.setRemoteDescription(new RTCSessionDescription(answer));
		} catch (err) {
			console.error('useWebRTC handleAnswer error:', err);
		}
	}, []);

	const handleIceCandidate = useCallback(async (fromUserId, candidate) => {
		const pc = peerConnections.current[fromUserId];
		if (!pc) return;
		try {
			await pc.addIceCandidate(new RTCIceCandidate(candidate));
		} catch (err) {
			console.error('useWebRTC handleIceCandidate error:', err);
		}
	}, []);

	const closePeerConnection = useCallback((userId) => {
		const pc = peerConnections.current[userId];
		if (pc) {
			pc.close();
			delete peerConnections.current[userId];
		}
		setRemoteStreams((prev) => {
			const next = { ...prev };
			delete next[userId];
			return next;
		});
		if (typeof onParticipantLeft === 'function') onParticipantLeft(userId);
	}, [onParticipantLeft]);

	const closeAll = useCallback(() => {
		Object.keys(peerConnections.current).forEach(closePeerConnection);
	}, [closePeerConnection]);

	const replaceVideoTrack = useCallback((newTrack) => {
		Object.values(peerConnections.current).forEach((pc) => {
			if (!pc || pc.connectionState === 'closed') return;
			const sender = pc.getSenders().find((s) => s.track?.kind === 'video');
			if (sender && newTrack) sender.replaceTrack(newTrack).catch(() => {});
		});
	}, []);

	// Attach socket listeners
	useEffect(() => {
		if (!socket) return;

		const onOffer = ({ from, offer, roomId: r }) => {
			if (r !== roomId) return;
			handleOffer(from, offer);
		};
		const onAnswer = ({ from, answer, roomId: r }) => {
			if (r !== roomId) return;
			handleAnswer(from, answer);
		};
		const onIce = ({ from, candidate, roomId: r }) => {
			if (r !== roomId) return;
			handleIceCandidate(from, candidate);
		};

		socket.on('call:offer', onOffer);
		socket.on('call:answer', onAnswer);
		socket.on('call:ice-candidate', onIce);

		return () => {
			socket.off('call:offer', onOffer);
			socket.off('call:answer', onAnswer);
			socket.off('call:ice-candidate', onIce);
		};
	}, [socket, roomId, handleOffer, handleAnswer, handleIceCandidate]);

	return {
		peerConnections,
		remoteStreams,
		createOffer,
		handleOffer,
		handleAnswer,
		handleIceCandidate,
		closePeerConnection,
		closeAll,
		replaceVideoTrack,
	};
}
